/**
 * src/game/data/chapter-difficulty-loader.ts — R-004 D2.
 *
 * 把 public/data/balance/chapters.json 的每章難度配方載進來,覆蓋
 * chapter-difficulty.ts 的硬編預設(CHAPTER_DIFFICULTY_RECIPES)。資料驅動:
 * 改 JSON 即可不動程式調整難度曲線。對齊 balance-loader.ts 的
 * fetch → 驗證 → fallback → cache 風格(任何失敗都不白屏,回硬編預設)。
 *
 * 學習/遊戲分離鐵則:本檔只讀「難度配方」(玩遊戲軌廣度),不碰 progress、
 * 不寫任何戰力欄位。
 */
import {
  CHAPTER_DIFFICULTY_RECIPES,
  type ChapterDifficultyRecipe,
} from './chapter-difficulty';

export interface ChaptersConfig {
  version: 1;
  chapters: ChapterDifficultyRecipe[];
}

/** 硬編預設(鏡像 chapters.json),fetch 失敗時 fallback。 */
export const DEFAULT_CHAPTERS_CONFIG: ChaptersConfig = {
  version: 1,
  chapters: CHAPTER_DIFFICULTY_RECIPES.map((r) => ({ ...r })),
};

/**
 * 驗證一份 chapters 配方:形狀完整 + 章與章之間「難度單調遞增」
 * (HP/攻擊/密度/獎勵不可下降;節奏 waveTempoMult 不可上升)。
 * 任何破壞單調性或缺欄位 → 回 false(caller fallback 到硬編預設),
 * 避免有人手滑把 JSON 改成「後面比前面簡單」。
 */
export function validateChaptersConfig(cfg: unknown): cfg is ChaptersConfig {
  if (!cfg || typeof cfg !== 'object') return false;
  const c = cfg as { version?: unknown; chapters?: unknown };
  if (c.version !== 1) return false;
  if (!Array.isArray(c.chapters) || c.chapters.length === 0) return false;

  const numericKeys: (keyof ChapterDifficultyRecipe)[] = [
    'chapter', 'enemyHpMult', 'enemyAttackMult', 'enemySpeedMult',
    'spawnDensityMult', 'waveTempoMult', 'intraChapterStep', 'bossTier', 'rewardMult',
  ];

  let prev: ChapterDifficultyRecipe | null = null;
  for (const raw of c.chapters as unknown[]) {
    if (!raw || typeof raw !== 'object') return false;
    const r = raw as Record<string, unknown>;
    for (const k of numericKeys) {
      if (typeof r[k] !== 'number' || !Number.isFinite(r[k] as number)) return false;
    }
    const rec = r as unknown as ChapterDifficultyRecipe;
    if (rec.bossTier !== 1 && rec.bossTier !== 2 && rec.bossTier !== 3) return false;
    if (prev) {
      // 章與章之間單調遞增(獎勵/難度不可倒退)。
      if (rec.enemyHpMult < prev.enemyHpMult) return false;
      if (rec.enemyAttackMult < prev.enemyAttackMult) return false;
      if (rec.spawnDensityMult < prev.spawnDensityMult) return false;
      if (rec.rewardMult < prev.rewardMult) return false;
      if (rec.bossTier < prev.bossTier) return false;
      // 節奏越後越快(gap 倍率越小);不可變慢。
      if (rec.waveTempoMult > prev.waveTempoMult) return false;
    }
    prev = rec;
  }
  return true;
}

let _cached: ChaptersConfig | null = null;

/**
 * 載入 chapters.json;失敗或驗證不過 → 回 DEFAULT_CHAPTERS_CONFIG。
 * 結果 cache,後續同步呼叫用 getChaptersSync()。
 */
export async function loadChaptersConfig(): Promise<ChaptersConfig> {
  if (_cached) return _cached;
  try {
    const res = await fetch('data/balance/chapters.json');
    if (!res.ok) {
      console.warn(`[chapter-difficulty-loader] chapters.json returned ${res.status}, using default`);
      _cached = DEFAULT_CHAPTERS_CONFIG;
      return _cached;
    }
    const json = (await res.json()) as unknown;
    if (!validateChaptersConfig(json)) {
      console.warn('[chapter-difficulty-loader] chapters.json failed validation, using default');
      _cached = DEFAULT_CHAPTERS_CONFIG;
      return _cached;
    }
    _cached = json;
    return _cached;
  } catch (err) {
    console.warn('[chapter-difficulty-loader] failed to fetch chapters.json:', err, '— using default');
    _cached = DEFAULT_CHAPTERS_CONFIG;
    return _cached;
  }
}

/** 同步取 cache;尚未載入回 DEFAULT_CHAPTERS_CONFIG。永不 throw。 */
export function getChaptersSync(): ChaptersConfig {
  return _cached ?? DEFAULT_CHAPTERS_CONFIG;
}

/** 清快取(測試用)。 */
export function resetChaptersCache(): void {
  _cached = null;
}
